import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { contactUsApi, type ContactUsPayload } from "../model/authApi";
import { toastService } from "@/shared/toast";

const BUTTON_ID = "landing-contact-send-button";

const toContactPayload = (formData: FormData): ContactUsPayload | null => {
  const fullName = String(formData.get("fullName") || "").trim();
  const email = String(formData.get("email") || "").trim();
  const phoneNumber = String(formData.get("phone") || "").trim();

  const [firstName = "", ...lastNameParts] = fullName.split(/\s+/).filter(Boolean);
  const lastName = lastNameParts.join(" ") || "";

  if (!firstName || !email || !phoneNumber) {
    return null;
  }

  return {
    first_name: firstName,
    last_name: lastName,
    email,
    phone_number: phoneNumber,
    sms_consent: true,
    email_whatsapp_consent: false,
  };
};

export const useLandingContactViewModel = () => {
  const mutation = useMutation({
    mutationFn: (payload: ContactUsPayload) => contactUsApi(payload, "inline", BUTTON_ID),
    onSuccess: () => {
      toastService.show("Message sent successfully.", "success");
    },
    onError: (error) => {
      console.error("Contact us submit failed:", error);
      toastService.show("Unable to send message. Please try again.", "error");
    },
  });

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (mutation.isPending) return;

      const form = event.currentTarget;
      const payload = toContactPayload(new FormData(form));
      if (!payload) {
        toastService.show("Please fill full name, email, and phone number.", "error");
        return;
      }

      try {
        await mutation.mutateAsync(payload);
      } finally {
        form.reset();
      }
    },
    [mutation]
  );

  return {
    isSubmitting: mutation.isPending,
    handleSubmit,
  };
};
